import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "What I learned making my first open source React component",
  "description": "",
  "published": "2018-02-21T00:00:00.000Z",
  "redirect_from": ["/blog/making-first-open-source-react-component/swizec/8204"],
  "categories": "Front End, Livecoding, OpenSource, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Dude, open source is hard. People really do this every day!? 🤨`}</p>
    <p>{`I open sourced my `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-lazyload-fadein"
      }}>{`react-lazyload-fadein`}</a>{` component this weekend. 75 stars already. I guess people like it 💪`}</p>
    <p><img parentName="p" {...{
        "src": "/0158ca590856a6f0dec6725134052b01/Swizec-react-lazyload-fadein-raw-master-react-lazyload-fadein.gif",
        "alt": null
      }}></img></p>
    <p>{`It builds on top of react-lazyload to give you a nice fade-in effect when your component is ready. Lazyload means users load less stuff and your page appears faster because you're not rendering complex expensive components until they're visible. Fade-in means they don't pop onto the page and startle your user.`}</p>
    <p><img parentName="p" {...{
        "src": "/4e6914d96f545ed84ded163061d55954/media-3o7TKQN2ni0e1B4gpy-giphy.gif",
        "alt": "When you lazyload components without FadeIn"
      }}></img></p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/fade-in-lazy-loaded-images-react-css-quick-guide/swizec/8163"
      }}>{`Building the component`}</a>{` was pretty quick. Learn the new API for React transitions, realize images have an `}<inlineCode parentName="p">{`onLoad`}</inlineCode>{` event, package it up in a component. Easy 👌`}</p>
    <p>{`Then open sourcing it… that took 4 hours. Because open source is hard.`}</p>
    <p>{`You can see half of the `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=vPHWS9Kk9lI"
      }}>{`process on YouTube`}</a>{`. The other half got banned because of the music.`}</p>
    <lite-youtube {...{
      "videoid": "vPHWS9Kk9lI",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Here are a few lessons I learned:`}</p>
    <h2 {...{
      "id": "use-nwb-to-get-started"
    }}>{`Use nwb to get started`}</h2>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Is there a create-react-app for components? I want to make an opensource component but not sure how to start.`}<br parentName="p"></br><br parentName="p"></br>{`create-react-component exists but seems way old`}<br parentName="p"></br><br parentName="p"></br>{`Maybe `}<a parentName="p" {...{
          "href": "https://twitter.com/thekitze"
        }}>{`@thekitze`}</a>{` `}<a parentName="p" {...{
          "href": "https://twitter.com/lukeed05"
        }}>{`@lukeed05`}</a>{` `}<a parentName="p" {...{
          "href": "https://twitter.com/kentcdodds"
        }}>{`@kentcdodds`}</a>{` have suggestions?`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/965102216603344896"
      }}>{`February 18, 2018`}</a></blockquote>
    <p>{`When you start a new React app, you use `}<inlineCode parentName="p">{`create-react-app`}</inlineCode>{`. It sets everything up for you. Babel, webpack, a dev server, some HTML, production builds, all the things.`}</p>
    <p>{`But you don't need most of that when you're building a component. You wouldn't want to minify before publishing to `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/"
      }}>{`npmjs`}</a>{`, for example. You definitely don't need to add a bunch of HTML and CSS.`}</p>
    <p>{`I mean, probably. You might need styling.`}</p>
    <p>{`Point is, you are trying to publish an isolated component. You still need `}<em parentName="p">{`some`}</em>{` build step for shipping and some way of running the component when testing. But you don't need CRA.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/insin/nwb/blob/master/docs/guides/ReactComponents.md#developing-react-components-and-libraries-with-nwb"
      }}>{`Use `}<inlineCode parentName="a">{`nwb`}</inlineCode>{` instead`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`nwb new react-component react-bla
`}</code></pre>
    <p>{`This sets you up. A `}<inlineCode parentName="p">{`src`}</inlineCode>{` folder for your code, a `}<inlineCode parentName="p">{`demo`}</inlineCode>{` folder for your demo app (plus dev server and hot loading, yay`}{`\\`}{`o/), a `}<inlineCode parentName="p">{`test`}</inlineCode>{` folder that you should use and I didn't, some build stuff for shipping…`}</p>
    <p>{`Basically, it gives you everything you need so you can focus on your component and don't have to worry about the details.`}</p>
    <h2 {...{
      "id": "make-a-demo-page-with-docs"
    }}>{`Make a demo page with docs`}</h2>
    <p>{`You need a demo page so you can show off your component in action. People are visual creatures, and they don't like to read descriptions.`}</p>
    <p>{`Show them what you built.`}</p>
    <p><a parentName="p" {...{
        "href": "https://react-lazyload-fadein.now.sh/"
      }}>{`react-lazyload-fadein`}</a>{`, for example, has a `}<a parentName="p" {...{
        "href": "https://react-lazyload-fadein.now.sh/"
      }}>{`demo page`}</a>{` that shows off different ways you can use it. The page is long enough for you to see the fade-in effect.`}</p>
    <p>{`The demo page doubles as docs. People can scroll to the integration they like and see how to build it right away.`}</p>
    <p>{`That part took a while because nwb's demo pages are quite basic. No Gatsby machinery to slurp in your README file and make a page.`}</p>
    <p>{`There's an idea 🤔`}</p>
    <h2 {...{
      "id": "decent-docs-take-time"
    }}>{`Decent docs take time`}</h2>
    <p>{`People will find out about your component in different ways. Some will see it on GitHub, others will come to your demo page, many will see it on `}<a parentName="p" {...{
        "href": "http://npmjs.com"
      }}>{`npmjs.com`}</a>{`.`}</p>
    <p>{`Your documentation has to live in at least two places:`}</p>
    <ol>
      <li parentName="ol">{`Your README.md file`}</li>
      <li parentName="ol">{`Your demo page`}</li>
    </ol>
    <p>{`Both GitHub and npmjs.com give special rights to your README. It shows up as your component's homepage.`}</p>
    <p>{`So it better be good. Add a gif or two. All the code samples someone needs to get started. Explain `}<em parentName="p">{`why`}</em>{` they'd wanna use it.`}</p>
    <p>{`This duplication is annoying and creates work for you. But it's worth it, I think. Especially if someone could figure out how to slurp in the README file and make that demo page for you 🤔`}</p>
    <h2 {...{
      "id": "support-different-patterns"
    }}>{`Support different patterns`}</h2>
    <p>{`The React community is a bit silly and loves to invent new patterns to bicker about. Function as children, render props, import this way or that way.`}</p>
    <p>{`Your component should support everything that's practical. That makes you a good citizen in other people's codebases.`}</p>
    <p>{`Someone likes to do `}<inlineCode parentName="p">{`import FadeIn from 'react-lazyload-fadein'`}</inlineCode>{`? They can.`}</p>
    <p>{`They prefer `}<inlineCode parentName="p">{`import { FadeIn } from 'react-lazyload-fadein'`}</inlineCode>{`? They can do that, too.`}</p>
    <p>{`Wanna use function as children, my favorite? Yep, it works.`}</p>
    <pre><code parentName="pre" {...{}}>{`<FadeIn>{onload => <MyThing onLoad={onload} />}</FadeIn>
`}</code></pre>
    <p>{`Using render props in your codebase? That works too.`}</p>
    <pre><code parentName="pre" {...{}}>{`<FadeIn render={onload => <MyThing onLoad={onload} />} />
`}</code></pre>
    <p>{`You shouldn't have to compromise your code style to use my component. My job isn't to encourage this or that pattern. My job is to give you a tool that you can use to make your life easier.`}</p>
    <p>{`And you know how hard it is to support both patterns?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`render() {
    const { children, render } = this.props;
    return (
        <div>
            {children && children(this.onLoad)}
            {render && render(this.onLoad)}
        </div>
    )
}
`}</code></pre>
    <p>{`Yeah, sooooo hard. Just do it.`}</p>
    <h2 {...{
      "id": "do-it-more-often"
    }}>{`Do it more often`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/shia-labeouf-do-it-1.gif",
        "alt": null
      }}></img></p>
    <blockquote>
      <p parentName="blockquote">{`People don't want to learn to fish. People want fish.`}</p>
    </blockquote>
    <p>{`Building things and writing about them is great. Great for your skills, great for your career, great for just about everything. Gives you more luck.`}</p>
    <p>{`Even just tweeting or posting on FB about what you're working on is better than keeping mum and chugging away. Ask anyone.`}</p>
    <p>{`You know what's even better?`}</p>
    <p>{`Giving people tools.`}</p>
    <p>{`You're not just telling them what to do, you're ensuring they have less to do. Everyone loves having less to do.`}</p>
    <p>{`I should do it more often, too. :)`}</p>
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      